import { Valuation } from './Valuation';
import { Properties } from './Properties';
import BaseObject from './BaseObject';
import { LocationInfo } from './LocationInfo';
import { ImageType } from '../types/ImageType';
import { PreviewSpecifications } from './PreviewSpecifications';
import { PayoutDetails } from './PayoutDetails';
import { AuctionType } from './Auction';
import { Nullable } from '../types/Nullable';
import { BaseObjectType } from '../types/BaseObjectType';

export interface Fact {
  key: string;
  text: string;
  value: number | string;
  type: string;
}

export interface ManufacturerWarrantyFact {
  key: string;
  text: string;
  type: 'manufacturer_warranty';
  value: {
    expirationDate: string;
    odometerReadingLimit: number;
  };
}

export interface Media {
  uri: string;
  type: ImageType;
  contentType: string;
  order: number;
  platform?: string;
  thumbnail?: string;
  videoId?: string;
}

export enum ProcessObjectStates {
  'ON_PIPELINE' = 'ON_PIPELINE',
  'VALUATION_AND_PRESENTATION' = 'VALUATION_AND_PRESENTATION',
  'AUCTION' = 'AUCTION',
  'AUCTION_RESOLUTION' = 'AUCTION_RESOLUTION',
  'COMPLETED' = 'COMPLETED',
  'ABORTED' = 'ABORTED'
}

export enum VehicleType {
  Car = 'CAR',
  LightTransport = 'LIGHT_TRANSPORT',
  HeavyTransport = 'HEAVY_TRANSPORT',
  RecreationalVehicle = 'RECREATIONAL_VEHICLE',
  Machines = 'MACHINES'
}

export const conditionClassifications = [
  'certified_plus',
  'certified',
  'standard',
  'repair_object'
] as const;

export type ConditionClassification = Uppercase<
  (typeof conditionClassifications)[number]
>;

export default interface ProcessObject {
  id: string;

  statisticalValuation: {
    bpGreen: null | number;
    bpOrange: number;
    bpWhite: number;
    marketPrice: number;
  } | null;

  baseObject: BaseObject;
  baseObjectId: string;

  previewImage?: string;
  previewTitle?: string;
  previewPackage?: Nullable<string>;
  previewPreamble?: string;
  previewSpecifications?: PreviewSpecifications | string;

  properties: Properties;
  media: Media[];

  facts?: {
    additional?: Fact[];
    default?: Fact[];
    other?: Fact[];
  };
  vehicleType: VehicleType;
  objectType: BaseObjectType;
  title: string;
  brand: string;
  modelName: string;
  familyName: string;
  heading1: string;
  heading2?: Nullable<string>;
  heading3?: Nullable<string>;

  state: ProcessObjectStates;

  orderableAdditionalServices: {
    tradein: boolean;
    transport: boolean;
    insurance: boolean;
    finance: boolean;
    warranty: boolean;
    extraTires: boolean;
  };

  car360Spin?: Nullable<boolean>;
  proactiveBrokering: null;

  presentationApproved: boolean;

  sellMethod: Nullable<AuctionType>;
  testDriveAllowed: boolean;
  package?: Nullable<string>;
  collisionWarranty: Nullable<string>;

  createdAt: string;

  buyNowAmount: Nullable<number>;
  initialBuyNowAmount: Nullable<number>;
  reducedPrice: boolean;

  isReleasable: boolean;
  exportable: boolean;
  deductibleVat: boolean;
  displayCar360Spin: boolean;

  locationInfo?: LocationInfo;
  secondaryAuction: boolean;

  valuation: Valuation;

  payoutDetails?: PayoutDetails;

  isTradeIn?: boolean;

  offsiteSalesContact: {
    city: string;
    cityLatitude: number;
    cityLongitude: number;
    country: string;
  } | null;
  description?: Record<string, string> | null;

  majorFeature: {
    key: string;
    title: string;
    value: string | number | null;
    equipment: string | null;
    description: string | null;
  }[];
  conditionClassification: ConditionClassification;
}
